import { LineSeriesType } from '@mui/x-charts-pro';
import { ScaleContinuousNumeric, ScaleTime, ScaleLinear, ScalePoint } from 'd3-scale';
import { getMarkerData } from '@/components/map/markers/TargetMarkerChooser';
import { TachographDataDuringFromToPeriod, TachographState } from './TachographService';

export type Row = {
  x: number;
  no_card?: number | null;
  availability?: number | null;
  rest?: number | null;
  work?: number | null;
  driving?: number | null;
};

export const colorFromState: Record<TachographState, string> = {
  NO_CARD: '#AAA',
  REST: '#00568F',
  AVAILABILITY: '#2E7D32',
  WORK: '#EAB308',
  DRIVING: '#EF4444',
};

export const colorFromNumber: Record<number, string> = {
  0: 'transparent',
  1: '#00568F',
  2: '#2E7D32',
  3: '#EAB308',
  4: '#EF4444',
};

export const series = (): LineSeriesType[] => {
  return [
    {
      id: 'NO_CARD',
      type: 'line',
      dataKey: 'no_card',
      area: true,
      showMark: false,
      disableHighlight: true,
      connectNulls: false,
      curve: 'linear',
      color: 'transparent',
    },
    {
      id: 'REST',
      type: 'line',
      dataKey: 'rest',
      area: true,
      showMark: false,
      disableHighlight: true,
      connectNulls: false,
      curve: 'linear',
      color: colorFromState[TachographState.REST],
    },
    {
      id: 'AVAILABILITY',
      type: 'line',
      dataKey: 'availability',
      area: true,
      showMark: false,
      disableHighlight: true,
      connectNulls: false,
      curve: 'linear',
      color: colorFromState[TachographState.AVAILABILITY],
    },
    {
      id: 'WORK',
      type: 'line',
      dataKey: 'work',
      area: true,
      showMark: false,
      disableHighlight: true,
      connectNulls: false,
      curve: 'linear',
      color: colorFromState[TachographState.WORK],
    },
    {
      id: 'DRIVING',
      type: 'line',
      dataKey: 'driving',
      area: true,
      showMark: false,
      disableHighlight: true,
      connectNulls: false,
      curve: 'linear',
      color: colorFromState[TachographState.DRIVING],
    },
  ];
};

export const icons: Record<number, string> = {
  0: '',
  1: getMarkerData('TACHO_REST', 23, 23, colorFromState[TachographState.REST]),
  2: getMarkerData('TACHO_AVAIL', 23, 23, colorFromState[TachographState.AVAILABILITY]),
  3: getMarkerData('TACHO_WORK', 23, 23, colorFromState[TachographState.WORK]),
  4: getMarkerData('TACHO_DRIVE', 23, 23, colorFromState[TachographState.DRIVING]),
};

export function isInvertibleScale(s: unknown): s is ScaleContinuousNumeric<number, number> | ScaleTime<number, number> {
  return typeof (s as { invert?: unknown }).invert === 'function';
}

export function isContinuousScale(s: unknown): s is ScaleLinear<number, number> | ScalePoint<number> {
  return !!s && typeof (s as { ticks?: unknown }).ticks === 'function';
}

export const dataForPreLoadedDiagram: TachographDataDuringFromToPeriod = {
  fromDate: new Date(),
  toDate: new Date(),
  summary: {
    drivingTimeMillis: 18333000,
    workTimeMillis: 14333000,
    restTimeMillis: 58333000,
    availabilityTimeMillis: 28333000,
  },
  states: [
    {
      state: TachographState.REST,
      startInDayMillis: 0,
      stopInDayMillis: 18333000,
    },
    {
      state: TachographState.AVAILABILITY,
      startInDayMillis: 18333000,
      stopInDayMillis: 27387000,
    },
    {
      state: TachographState.WORK,
      startInDayMillis: 27387000,
      stopInDayMillis: 28414000,
    },
    {
      state: TachographState.DRIVING,
      startInDayMillis: 28414000,
      stopInDayMillis: 46648000,
    },
    {
      state: TachographState.WORK,
      startInDayMillis: 46648000,
      stopInDayMillis: 48648000,
    },
    {
      state: TachographState.DRIVING,
      startInDayMillis: 48648000,
      stopInDayMillis: 62466000,
    },
    {
      state: TachographState.AVAILABILITY,
      startInDayMillis: 62466000,
      stopInDayMillis: 67466000,
    },
    {
      state: TachographState.REST,
      startInDayMillis: 67466000,
      stopInDayMillis: 86399000,
    },
  ],
};
